import { useMutation } from '@tanstack/react-query';
import { getAuthenticatedApiClient } from '../client';
import { callApi } from '../apiErrorEnvelope';
import { startNewTelemetryOperation } from '../../telemetry/appInsights';
import { ScanOrderBody } from '../generated/api-client';
import type { ScanOrderData, ScanShipmentData } from '../generated/api-client';

// String literal union — values must match the backend string serialization
// (same pattern as useCarrierCooling.ts's local Cooling type).
export type Cooling = 'None' | 'L1' | 'L2';

export interface PackingOrderItem {
  name: string;
  quantity: number;
  imageUrl: string | null;
  setName: string | null;
}

export interface PackingEligibility {
  isEligible: boolean;
}

export interface ShippingAddress {
  street: string | null;
  city: string | null;
  zip: string | null;
}

// Shaped to match usePackingOrder's PackingOrder so existing sub-components work unchanged
export interface PackingOrder {
  code: string;
  customerName: string;
  shippingMethodName: string;
  shippingAddress: ShippingAddress | null;
  cooling: Cooling;
  isCooled: boolean;
  customerNote: string | null;
  eshopNote: string | null;
  eligibility: PackingEligibility;
  items: PackingOrderItem[];
}

export interface ScanShipmentPackage {
  trackingNumber: string | null;
  labelUrl: string | null;
  labelZpl: string | null;
}

export const mapShipmentPackages = (
  packages: Array<{ trackingNumber?: string; labelUrl?: string; labelZpl?: string }> | undefined,
): ScanShipmentPackage[] =>
  (packages ?? []).map((pkg) => ({
    trackingNumber: pkg.trackingNumber ?? null,
    labelUrl: pkg.labelUrl ?? null,
    labelZpl: pkg.labelZpl ?? null,
  }));

export interface ScanShipment {
  shipmentGuid: string;
  packages: ScanShipmentPackage[];
  alreadyExisted: boolean;
  pendingCompletion?: boolean;
}

export interface ScanPackingOrderResult {
  order: PackingOrder;
  shipment: ScanShipment | null;
}

const toPackingOrder = (data: ScanOrderData): PackingOrder => ({
  code: data.code ?? '',
  customerName: data.customerName ?? '',
  shippingMethodName: data.shippingMethodName ?? '',
  shippingAddress: data.shippingAddress
    ? {
        street: data.shippingAddress.street ?? null,
        city: data.shippingAddress.city ?? null,
        zip: data.shippingAddress.zip ?? null,
      }
    : null,
  cooling: (data.cooling as unknown as Cooling) ?? 'None',
  isCooled: data.isCooled ?? false,
  customerNote: data.customerNote ?? null,
  eshopNote: data.eshopNote ?? null,
  eligibility: { isEligible: data.eligibility?.isEligible ?? false },
  items: (data.items ?? []).map((item) => ({
    name: item.name ?? '',
    quantity: item.quantity ?? 0,
    imageUrl: item.imageUrl ?? null,
    setName: item.setName ?? null,
  })),
});

const toScanShipment = (data: ScanShipmentData): ScanShipment => ({
  shipmentGuid: data.shipmentGuid ?? '',
  packages: mapShipmentPackages(data.packages),
  alreadyExisted: data.alreadyExisted ?? false,
  pendingCompletion: data.pendingCompletion,
});

const SCAN_ERROR_MESSAGES: Partial<Record<string, string>> = {
  ShoptetOrderNotFound: 'Objednávka nebyla nalezena.',
  ShipmentCarrierNotResolved: 'Dopravce se nepodařilo určit pro tuto objednávku.',
  ShipmentCreationFailed: 'Shoptet nemohl vytvořit zásilku — zkuste znovu.',
  ShipmentOrderWeightUnavailable: 'Nelze zjistit hmotnost objednávky.',
  PackingUserNotEligible: 'Vybraný balič není aktivní nebo nemá oprávnění balit. Vyberte baliče znovu.',
};

// Shoptet permanently rejected the recipient/shipment data (e.g. missing city/zip) — this is
// not a "try again" condition, so the wording explicitly says to fix the order in Shoptet.
// When the backend forwards the Shoptet message (Params["ShoptetMessage"] — see
// ShipmentCreationService.CreateAndPersistAsync), it's appended so the operator sees exactly
// which fields are missing instead of only the generic category.
const SHIPMENT_VALIDATION_FAILED_BASE =
  'Adresu příjemce nelze použít pro vytvoření zásilky (chybí povinné údaje) — opravte ji v Shoptetu.';

const shipmentValidationFailedDetailed = (detail: string) =>
  `Adresu příjemce nelze použít pro vytvoření zásilky — opravte v Shoptetu: ${detail}.`;

const GENERIC_SCAN_ERROR = 'Chyba při skenování objednávky.';

export type ScanPackingOrderVariables = {
  orderCode: string;
  numberOfPackages?: number;
  packingUserId?: string | null;
};

const scanPackingOrder = async ({
  orderCode,
  numberOfPackages = 1,
  packingUserId = null,
}: ScanPackingOrderVariables): Promise<ScanPackingOrderResult> => {
  const apiClient = getAuthenticatedApiClient(false);
  const response = await callApi(
    () =>
      apiClient.packaging_ScanOrder(
        orderCode,
        numberOfPackages,
        new ScanOrderBody({ packingUserId: packingUserId ?? undefined }),
      ),
    ({ errorCode, params }) => {
      if (errorCode === 'ShipmentValidationFailed') {
        const detail = params?.ShoptetMessage;
        return detail ? shipmentValidationFailedDetailed(detail) : SHIPMENT_VALIDATION_FAILED_BASE;
      }
      return (errorCode && SCAN_ERROR_MESSAGES[errorCode]) ?? GENERIC_SCAN_ERROR;
    },
  );

  if (!response.order) {
    throw new Error(GENERIC_SCAN_ERROR);
  }

  return {
    order: toPackingOrder(response.order),
    shipment: response.shipment ? toScanShipment(response.shipment) : null,
  };
};

export const useScanPackingOrder = () =>
  useMutation<ScanPackingOrderResult, Error, ScanPackingOrderVariables>({
    mutationFn: scanPackingOrder,
    onMutate: () => {
      // Each scan is its own AI operation so repeated scans on the same page do
      // not collapse into a single end-to-end transaction.
      startNewTelemetryOperation('packaging-scan');
    },
  });
